import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Button from './button/Button';

type ErrorBoundaryProps = {
    children: ReactNode;
};

type ErrorBoundaryState = {
    hasError: boolean;
};

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(): ErrorBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        if (process.env.NODE_ENV === 'development') {
            console.error('ErrorBoundary caught:', error, errorInfo);
        }
    }

    handleRetry = () => {
        this.setState({ hasError: false });
        window.location.href = '/';
    };

    render() {
        if (this.state.hasError) {
            return <ErrorBoundaryFallback onRetry={this.handleRetry} />;
        }
        return this.props.children;
    }
}

function ErrorBoundaryFallback({ onRetry }: { onRetry: () => void }) {
    const { t } = useTranslation();

    return (
        <div className='flex min-h-[50vh] flex-col items-center justify-center gap-6 px-6 text-center'>
            <h1 className='text-lg font-semibold text-[#103793]'>
                {t('errorBoundaryTitle', 'Something went wrong')}
            </h1>
            <p className='text-sm text-[#4F4F4F]'>
                {t('errorBoundaryMessage', 'Please return to the start and try again.')}
            </p>
            <Button onClick={onRetry}>
                {t('errorButton')}
            </Button>
        </div>
    );
}
